import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Supports cross-section deep links. When a section is opened with `?focus=<id>`, the
 * element with `id="entity-<id>"` is scrolled into view and briefly highlighted, then the
 * query param is cleared so it doesn't re-fire on re-render.
 *
 * List sections call this once and tag their cards with `id={focusId(entityId)}`.
 */
export function useFocusHighlight(): void {
  const [params, setParams] = useSearchParams();
  const focus = params.get('focus');

  useEffect(() => {
    if (!focus) return;
    const el = document.getElementById(`entity-${focus}`);
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Defer so the section has painted before we scroll/highlight.
    const raf = requestAnimationFrame(() => {
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('gmb-focus');
        timer = setTimeout(() => el.classList.remove('gmb-focus'), 2200);
      }
      const next = new URLSearchParams(params);
      next.delete('focus');
      setParams(next, { replace: true });
    });
    return () => {
      cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);
}

/** Build the DOM id a focus target must carry. */
export function focusId(entityId?: string): string | undefined {
  return entityId ? `entity-${entityId}` : undefined;
}
